<template>
  <section v-show="fragmentsLoaded" class="eptp_content">
    <div class="container">
      <ul class="breadcrumbs">
        <li>
          <a :href="homeHref">{{ $t('pages.home.title') }}</a>
        </li>
        <li>
          <a :href="$url($t('pages.myServices.url'))">{{ $t('pages.myServices.title') }}</a>
        </li>
      </ul>
      <div class="messages-wrapper">
        <div class="alert alert-warning" role="alert" >
          <i class="icon-info"></i>
          <div class="error-list" >
              {{ $t('pages.myServices.warning.text') }}
              <a :href="getWarningUrl()">
                {{ $t('pages.myServices.warning.link') }}
              </a>
          </div>
        </div>
      </div>

      <BaseGlobalAlert />

      <h1>
        {{ $t('pages.myServices.title') }}
      </h1>

      <form class="clearfix" @submit.prevent="getServices(1)">
        <ul class="filter_form clearfix">
          <li class="filter_form__items">
            <label class="filter_form__label" for="type-filter">{{
              $t('pages.myServices.filters.type')
            }}</label>
            <BaseSelect
              v-model="serviceParams.type"
              :options="serviceStatusTypes"
              :reduce="(option) => option.code"
              id="type-filter"
              clearable
            />
          </li>
          <li class="filter_form__items">
            <label class="filter_form__label" for="group-filter">{{
              $t('pages.myServices.filters.group')
            }}</label>
            <BaseSelect
              v-model="serviceParams.group"
              :options="serviceGroups"
              :reduce="(option) => option.code"
              id="group-filter"
              clearable
            />
          </li>
          <li class="filter_form__items">
            <label class="filter_form__label" for="status-filter">{{
              $t('pages.myServices.filters.status')
            }}</label>
            <BaseSelect
              v-model="serviceParams.status"
              :options="serviceStatuses"
              :reduce="(option) => option.code"
              id="status-filter"
              clearable
            />
          </li>
          <li class="filter_form__items">
            <label class="filter_form__label">{{ $t('pages.myServices.filters.updatedAt') }}</label>
            <div class="row">
              <div class="col-xs-6">
                <BaseDatepicker
                  v-model="serviceParams.periodStart"
                  :placeholder="$t('pages.myServices.filters.periodStart')"
                />
              </div>
              <div class="col-xs-6">
                <BaseDatepicker
                  v-model="serviceParams.periodEnd"
                  :placeholder="$t('pages.myServices.filters.periodEnd')"
                />
              </div>
            </div>
          </li>
          <li class="filter_form__items">
            <label class="filter_form__label">{{ $t('pages.myServices.filters.number') }}</label>
            <input v-model="serviceParams.number" type="text" class="form-control" />
          </li>
          <li class="filter_form__items">
            <label class="filter_form__label">{{ $t('pages.myServices.filters.orderDate') }}</label>
            <div class="row">
              <div class="col-xs-6">
                <BaseDatepicker
                  v-model="serviceParams.orderFrom"
                  :placeholder="$t('pages.myServices.filters.periodStart')"
                />
              </div>
              <div class="col-xs-6">
                <BaseDatepicker
                  v-model="serviceParams.orderTo"
                  :placeholder="$t('pages.myServices.filters.periodEnd')"
                />
              </div>
            </div>
          </li>
          <li class="filter_form__items">
            <label class="filter_form__label">{{ $t('pages.myServices.filters.name') }}</label>
            <input v-model="serviceParams.name" type="text" class="form-control" />
          </li>

          <li class="filter_form__items filter_form__items--full">
            <button
              :disabled="loadingServices"
              type="submit"
              class="btn btn-feat btn-mobile-full pull-right"
            >
              {{ $t('actions.updateResults') }}
            </button>
          </li>
        </ul>
      </form>

      <BaseTable
        :headers="serviceTableHeaders"
        :items="services"
        :pagination="servicePagination"
        :loading="loadingServices"
        :on-row-click="viewService"
        enable-sorting
        @update:page="getServices"
        @update:sorting="setOrderBy"
      >
        <template #item.name="{ item }">
          {{ item.name }}
          <div v-if="item.additionalInfo" style="font-size: 13px;">{{ item.additionalInfo }}</div>
        </template>
        <template #item.info="{ item }">
          <a
            v-tooltip.bottom="$t('pages.myServices.labels.serviceDescription')"
            :href="$url(`${$t('pages.service.url')}?id=${item.serviceId}`)"
            class="icon-button-link"
            @click.stop
          >
            <i class="icon-info icon-button" />
          </a>
        </template>
        <template #item.group="{ item }">
          {{ item.group.name }}
        </template>
        <template #item.creationDate="{ item }">
          {{ $formatDate(item.creationDate, 'yyyy-MM-dd HH:mm') }}
        </template>
        <template #item.updateDate="{ item }">
          {{ $formatDate(item.updateDate, 'yyyy-MM-dd HH:mm') }}
        </template>
        <template #item.status="{ item }">
          <div class="d-flex align-center">
            <div
              v-if="item.status"
              :style="{
                'border-color': getStatusChipColor(item),
                color: getStatusChipColor(item),
                'margin-right': item.statusDescription ? '6px' : '21px',
              }"
              class="status-chip"
            >
              {{ item.status.name }}
            </div>
            <span
              v-if="item.statusDescription"
              v-tooltip.bottom="{ content: item.statusDescription, trigger: 'click' }"
              class="icon-button-link"
              @click.stop
            >
              <i class="icon-info icon-button" :style="{ color: getStatusChipColor(item) }" />
            </span>
          </div>
        </template>
      </BaseTable>
    </div>

    <ImportantMessagesModal
      :loading="loadingImportantMessage"
      :message="displayedImportantMessage"
      @read="markImportantMessageAsRead"
    />
  </section>
</template>

<script>
import { VTooltip } from 'v-tooltip';
import BaseSelect from '../../components/base/BaseSelect';
import BaseDatepicker from '../../components/base/BaseDatepicker';
import pageMixin from '../../mixins/page.mixin';
import classifierService from '../../services/classifier.service';
import serviceService from '../../services/service.service';
import BaseTable from '../../components/base/BaseTable';
import BaseGlobalAlert from '../../components/base/BaseGlobalAlert';

export default {
  name: 'MyServices',

  components: { BaseGlobalAlert, BaseTable, BaseDatepicker, BaseSelect },

  directives: {
    tooltip: VTooltip,
  },

  mixins: [pageMixin],

  data() {
    return {
      loadingServices: false,
      services: [],
      servicePagination: {},
      serviceStatusTypes: [],
      serviceStatuses: [],
      serviceGroups: [],
      serviceParams: {},
    };
  },

  computed: {
    serviceTableHeaders() {
      const translationKey = 'pages.myServices.table.headers';
      return [
        { text: this.$t(`${translationKey}.number`), value: 'number' },
        { text: this.$t(`${translationKey}.name`), value: 'name' },
        { text: '', value: 'info' },
        { text: this.$t(`${translationKey}.group`), value: 'group' },
        { text: this.$t(`${translationKey}.creationDate`), value: 'creationDate' },
        { text: this.$t(`${translationKey}.updateDate`), value: 'updateDate' },
        { text: this.$t(`${translationKey}.status`), value: 'status', classes: 'text-center' },
      ];
    },
  },

  created() {
    document.title = `EPTP - ${this.$t('pages.myServices.title')}`;
    this.getServices();
    classifierService.getServiceStatusTypes().then((res) => {
      this.serviceStatusTypes = res.data.serviceStatusType;
    });
    classifierService.getServiceStatuses().then((res) => {
      this.serviceStatuses = res.data.serviceStatus;
    });
    classifierService.getServiceGroups().then((res) => {
      this.serviceGroups = res.data.serviceGroup;
    });
  },

  mounted() {
    this.setupPage();
  },

  methods: {
    getWarningUrl(){
      return window.vueAppConfig.servicesWarningUrl;
    },
    
    getServices(page = 1) {
      this.loadingServices = true;
      const params = { ...this.serviceParams, page };
      serviceService
        .getList(params)
        .then((res) => {
          this.services = res.data.service;
          this.servicePagination = {
            page: res.data.page,
            total: res.data.total,
            pageSize: res.data.pageSize,
          };
        })
        .catch(() => {
          this.services = [];
          this.servicePagination = {};
        })
        .finally(() => {
          this.loadingServices = false;
        });
    },

    getStatusChipColor(service) {
      return service.type?.color || 'grey';
    },

    setOrderBy(string) {
      this.$set(this.serviceParams, 'orderBy', string);
      this.getServices();
    },

    viewService(service) {
      window.open(service.url, '_blank');
    },
  },
};
</script>

<style>
@import '../../../assets/css/style.min.css';
@import '../../assets/index.css';
@import '../../../assets/css/tooltip.css';

.status-chip {
  border: 1px solid;
  text-align: center;
  border-radius: 16px;
  padding: 4px;
  max-width: 12em;
  width: 100%;
  margin-left: 21px;
}
</style>
